import React, { useEffect, useRef, useState } from "react";

/* ------------------------------------------------------------------ *
 * Unlayer-powered drag & drop email editor (same engine SendX uses).
 * The Unlayer editor is loaded at runtime from its CDN (editor.unlayer.com)
 * so nothing needs to be bundled at build time. It exposes a global
 * `unlayer` object with init / loadDesign / saveDesign / exportHtml.
 *
 * NOTE: no literal double-brace object syntax is used in JSX. Merge-tag
 * strings are built with mt() so raw " " never appears in source.
 * ------------------------------------------------------------------ */

const UNLAYER_SRC = "https://editor.unlayer.com/embed.js";
const CONTAINER_ID = "sx-unlayer-container";

// Optional: set your Unlayer projectId here to unlock your saved project
// templates / uploads. Leave null to use the default embedded editor.
const UNLAYER_PROJECT_ID = null;

// merge-tag helper (avoids writing raw double-brace tokens in source)
const mt = (n) => "{" + "{" + n + "}" + "}";

function loadUnlayerScript() {
  return new Promise((resolve, reject) => {
    if (window.unlayer) {
      resolve();
      return;
    }
    let s = document.getElementById("unlayer-embed-script");
    if (s) {
      s.addEventListener("load", () => resolve());
      s.addEventListener("error", () => reject(new Error("unlayer load failed")));
      if (window.unlayer) resolve();
      return;
    }
    s = document.createElement("script");
    s.id = "unlayer-embed-script";
    s.src = UNLAYER_SRC;
    s.async = true;
    s.onload = () => resolve();
    s.onerror = () => reject(new Error("unlayer load failed"));
    document.body.appendChild(s);
  });
}

export default function DragEmailEditor({ initial, onSave, onClose }) {
  const [name, setName] = useState((initial && initial.title) || "");
  const [category, setCategory] = useState((initial && initial.category) || "Custom");
  const [ready, setReady] = useState(false);
  const [failed, setFailed] = useState(false);
  const bootedRef = useRef(false);

  const categories = [
    "Custom", "Lifecycle", "SaaS", "Agency", "Inspiration",
    "Global Observances", "Dark Mode", "Photography", "Wedding"
  ];

  useEffect(() => {
    let cancelled = false;

    const isDark =
      typeof document !== "undefined" &&
      !!document.querySelector(".sx-app.sx-dark");

    function boot() {
      if (cancelled || bootedRef.current || !window.unlayer) return;
      bootedRef.current = true;

      const options = {
        id: CONTAINER_ID,
        displayMode: "email",
        appearance: { theme: isDark ? "modern_dark" : "modern_light" },
        features: { preview: true, textEditor: { spellChecker: true } },
        mergeTags: {
          first_name: { name: "First Name", value: mt("first_name") },
          last_name: { name: "Last Name", value: mt("last_name") },
          email: { name: "Email", value: mt("email") }
        }
      };
      if (UNLAYER_PROJECT_ID) options.projectId = UNLAYER_PROJECT_ID;

      try {
        window.unlayer.init(options);
      } catch (e) {
        setFailed(true);
        return;
      }

      window.unlayer.addEventListener("editor:ready", () => {
        if (cancelled) return;
        setReady(true);
        if (initial && initial.design) {
          try {
            window.unlayer.loadDesign(initial.design);
          } catch (e) {
            /* ignore malformed design */
          }
        }
      });
    }

    loadUnlayerScript()
      .then(() => {
        // give the embed a tick to attach the global
        if (window.unlayer) boot();
        else setTimeout(boot, 60);
      })
      .catch(() => {
        if (!cancelled) setFailed(true);
      });

    return () => {
      cancelled = true;
      const el = document.getElementById(CONTAINER_ID);
      if (el) el.innerHTML = "";
      bootedRef.current = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  function handleSave() {
    if (!window.unlayer) return;
    window.unlayer.saveDesign((design) => {
      window.unlayer.exportHtml((data) => {
        onSave({
          title: name.trim() || "Untitled template",
          category: category || "Custom",
          html: (data && data.html) || "",
          design: design
        });
      });
    });
  }

  return (
    <div className="dee-overlay">
      <div className="dee-shell">
        <div className="dee-topbar">
          <div className="dee-title">
            <strong>MailX</strong>
            <input
              className="dee-name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Template name"
            />
            <select value={category} onChange={(e) => setCategory(e.target.value)}>
              {categories.map((c) => (
                <option key={c} value={c}>{c}</option>
              ))}
            </select>
          </div>
          <div className="dee-actions">
            <button type="button" className="dee-ghost" onClick={onClose}>Cancel</button>
            <button
              type="button"
              className="dee-primary"
              onClick={handleSave}
            >
              Save Template
            </button>
          </div>
        </div>

        <div className="dee-unlayer-wrap">
          {failed && (
            <div className="dee-unlayer-error">
              <p>The drag &amp; drop editor could not load.</p>
              <p>Please check your internet connection and try again.</p>
            </div>
          )}
          <div id={CONTAINER_ID} className="dee-unlayer" />
        </div>
      </div>
    </div>
  );
}
